import { useEffect } from 'react'

import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useInitiateCall } from '@liveHelper/hooks/useInitiateCall'
import {
  actionsSelector,
  useStore,
  useStoreApi,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { useTarget } from '@oneToOne/shared/contexts/TargetContext'
import { useReconnectClosedSocket } from '@oneToOne/shared/contexts/useReconnectClosedSocket'
import { throttle } from 'es-toolkit'

import { EventType } from '@core/customEvent'

/**
 * Hook is responsible for subscribing to events which allows controlling of
 * chat and call from outside of widget
 */
export const useExternalControl = () => {
  const { chatWithAgent } = useStore(visitorPrivateActionsSelector)
  const storeApi = useStoreApi()
  const liveHelper = useTarget()
  const initiateCall = useInitiateCall({})
  const chatChannelConfig = useChatChannelConfig()
  const reconnectClosedSocket = useReconnectClosedSocket()

  useEffect(() => {
    const actions = actionsSelector(storeApi.getState())

    const liveHelperNode = liveHelper?.getRootNode().host
    const showWidget = throttle(
      (event: Event | undefined) => {
        if (event?.detail) {
          storeApi.setState({
            params: {
              ...storeApi.getState().params,
              ...event.detail,
            },
          })
        }

        reconnectClosedSocket()
        actions.showWidget()
      },
      250,
      { edges: ['leading'] },
    )

    const startCall = throttle(
      (event: Event) => {
        showWidget(event)
        actions.sendUIStateTransitionEvent('EXPAND')
        initiateCall()
      },
      250,
      { edges: ['leading'] },
    )

    const startChat = throttle(
      (event: Event) => {
        showWidget(event)
        // TODO: Able to start chat with agent even if widget availability is hidden
        chatWithAgent?.()
      },
      250,
      { edges: ['leading'] },
    )

    liveHelperNode?.addEventListener(
      EventType.FW_LIVE_HELPER_EXTERNAL_START_CALL,
      startCall,
    )
    liveHelperNode?.addEventListener(
      EventType.FW_LIVE_HELPER_EXTERNAL_START_CHAT,
      startChat,
    )
    liveHelperNode?.addEventListener(
      EventType.FW_LIVE_HELPER_EXTERNAL_SHOW_WIDGET,
      showWidget,
    )
    return () => {
      liveHelperNode?.removeEventListener(
        EventType.FW_LIVE_HELPER_EXTERNAL_START_CALL,
        startCall,
      )
      liveHelperNode?.removeEventListener(
        EventType.FW_LIVE_HELPER_EXTERNAL_START_CHAT,
        startChat,
      )
      liveHelperNode?.removeEventListener(
        EventType.FW_LIVE_HELPER_EXTERNAL_SHOW_WIDGET,
        showWidget,
      )
    }
  }, [
    storeApi,
    chatWithAgent,
    liveHelper,
    initiateCall,
    chatChannelConfig?.config?.agent_available,
    reconnectClosedSocket,
  ])
}
